import { useState, useEffect, useCallback } from "react";
import { API, AUTH, toLocalDateStr, fmtMoney } from "../adminUtils";
import "../admin-base.css";
import "../admin-layout.css";
import "../admin-controls.css";
import "../admin-responsive.css";

// ── Stock Management ──────────────────────────────────────
function StockSection() {
  const [products,    setProducts]   = useState([]);
  const [loading,     setLoading]    = useState(true);
  const [showForm,    setShowForm]   = useState(false);
  const [editTarget,  setEditTarget] = useState(null);
  const [form,        setForm]       = useState({ name: "", category: "", price: "", cost: "", stock: "", taxable: true });
  const [saving,      setSaving]     = useState(false);
  const [error,       setError]      = useState("");

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/merch/products`, { headers: AUTH });
      const data = await res.json();
      setProducts(data.products ?? []);
    } catch { setProducts([]); }
    finally   { setLoading(false); }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const openAdd = () => {
    setEditTarget(null);
    setForm({ name: "", category: "", price: "", stock: "" });
    setError("");
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditTarget(p);
    setForm({ name: p.name, category: p.category, price: p.price, cost: p.cost ?? 0, stock: p.stock, taxable: p.taxable ?? true });
    setError("");
    setShowForm(true);
  };
  const handleSave = async () => {
  if (!form.name || form.price === "") { setError("Name and price are required."); return; }
  setSaving(true); setError("");
  try {
    const body = {
      name: form.name,
      category: form.category,
      price: Number(form.price),
      cost: Number(form.cost) || 0,
      stock: Number(form.stock),
      taxable: form.taxable,
    };
    const res = editTarget
      ? await fetch(`${API}/merch/products/${editTarget._id}`, { method: "PATCH", headers: AUTH, body: JSON.stringify(body) })
      : await fetch(`${API}/merch/products`, { method: "POST", headers: AUTH, body: JSON.stringify(body) });
    if (!res.ok) { const d = await res.json(); setError(d.error ?? "Failed."); return; }
    setShowForm(false);
    fetchProducts();
  } catch { setError("Network error."); }
  finally   { setSaving(false); }
};
  const handleToggleActive = async (p) => {
    await fetch(`${API}/merch/products/${p._id}`, { method: "PATCH", headers: AUTH, body: JSON.stringify({ active: !p.active }) });
    fetchProducts();
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    await fetch(`${API}/merch/products/${p._id}`, { method: "DELETE", headers: AUTH });
    fetchProducts();
  };

  const categories = [...new Set(products.map(p => p.category || "General"))];

  return (
    <div>
      <div className="adm-roster-header">
        <h2 className="adm-section-title">Stock</h2>
        <button className="adm-btn-primary" onClick={openAdd}>+ Add Product</button>
      </div>

      {showForm && (
        <div className="adm-create-form">
          <p className="adm-form-title">{editTarget ? "Edit Product" : "New Product"}</p>
          <div className="adm-form-grid">
            <label className="adm-form-label">Name
              <input className="adm-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Yoga Mat" />
            </label>
            <label className="adm-form-label">Category
              <input className="adm-input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g. Equipment" list="cat-list" />
              <datalist id="cat-list">{categories.map(c => <option key={c} value={c} />)}</datalist>
            </label>
            <label className="adm-form-label">Cost per unit ($)
              <input className="adm-input" type="number" min="0" step="0.01" autoComplete="off" value={form.cost} onChange={e => setForm(f => ({ ...f, cost: e.target.value }))} />
            </label>
            <label className="adm-form-label">Price ($)
              <input className="adm-input" type="number" min="0" step="0.01" autoComplete="off" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} />
            </label>
           <label className="adm-form-label" style={{ gridColumn: "1/-1", display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <input type="checkbox" checked={form.taxable} onChange={e => setForm(f => ({ ...f, taxable: e.target.checked }))} />
              Taxable (7% FL sales tax)
            </label>
            <label className="adm-form-label">Stock
              <input className="adm-input" type="number" min="0" value={form.stock} onChange={e => setForm(f => ({ ...f, stock: e.target.value }))} />
            </label>
          </div>
          {error && <p className="adm-error">{error}</p>}
          <div className="adm-modal-actions" style={{ justifyContent: "flex-start", marginTop: "0.75rem" }}>
            <button className="adm-btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="adm-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : editTarget ? "Save Changes" : "Add Product"}
            </button>
          </div>
        </div>
      )}

      {loading && <p className="adm-loading">Loading…</p>}
      {!loading && products.length === 0 && <p className="adm-empty">No products yet.</p>}
      {!loading && products.length > 0 && (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr><th>SKU</th><th>Product</th><th>Category</th><th>Price</th><th>Cost</th><th>Stock</th><th>Taxable</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p._id} className={!p.active ? "row-cancelled" : ""}>
                  <td><code>{p.sku ?? "—"}</code></td>
                  <td><strong>{p.name}</strong></td>
                  <td>{p.category || "—"}</td>
                  <td>{fmtMoney(p.price)}</td>
                  <td>{fmtMoney(p.cost ?? 0)}</td>
                  <td>{p.taxable ? "Yes" : "No"}</td>
                  <td>
                    <span style={{ color: p.stock === 0 ? "#e53e3e" : p.stock <= 3 ? "#b97a0a" : "inherit", fontWeight: p.stock <= 3 ? 700 : 400 }}>
                      {p.stock}
                    </span>
                  </td>
                  <td><span className={`adm-status-badge ${p.active ? "status-confirmed" : "status-cancelled"}`}>{p.active ? "Active" : "Inactive"}</span></td>
                  <td>
                    <div className="adm-row-actions">
                      <button className="adm-btn adm-btn-ghost-sm" onClick={() => openEdit(p)}>Edit</button>
                      <button className="adm-btn adm-btn-ghost-sm" onClick={() => handleToggleActive(p)}>{p.active ? "Deactivate" : "Reactivate"}</button>
                      <button className="adm-btn-danger-sm" onClick={() => handleDelete(p)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Sales Terminal ────────────────────────────────────────
function SalesSection() {
  const today = toLocalDateStr(new Date());
  const [products, setProducts]     = useState([]);
  const [sales, setSales]           = useState([]);
  const [loadingP, setLoadingP]     = useState(true);
  const [loadingS, setLoadingS]     = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [quantity, setQuantity]     = useState(1);
  const [payment, setPayment]       = useState("cash");
  const [processing, setProcessing] = useState(false);
  const [result, setResult]         = useState(null);
  const [error, setError]           = useState("");

  const fetchProducts = useCallback(async () => {
    setLoadingP(true);
    try {
      const res  = await fetch(`${API}/merch/products`, { headers: AUTH });
      const data = await res.json();
      setProducts((data.products ?? []).filter(p => p.active && p.stock > 0));
    } catch { setProducts([]); }
    finally   { setLoadingP(false); }
  }, []);

  const fetchSales = useCallback(async () => {
    setLoadingS(true);
    try {
      const res  = await fetch(`${API}/merch/sales?from=${today}&to=${today}`, { headers: AUTH });
      const data = await res.json();
      setSales(data.sales ?? []);
    } catch { setSales([]); }
    finally   { setLoadingS(false); }
  }, [today]);

  useEffect(() => { fetchProducts(); fetchSales(); }, [fetchProducts, fetchSales]);

  const selected = products.find(p => p._id === selectedId);

  const handleSell = async () => {
    if (!selectedId) { setError("Please select a product."); return; }
    if (quantity < 1) { setError("Quantity must be at least 1."); return; }
    if (selected && quantity > selected.stock) { setError(`Only ${selected.stock} in stock.`); return; }
    setProcessing(true); setError(""); setResult(null);
    try {
      const res  = await fetch(`${API}/merch/sales`, {
        method: "POST", headers: AUTH,
        body: JSON.stringify({ productId: selectedId, quantity, paymentMethod: payment, date: today }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error ?? "Failed."); return; }
      setResult({ sale: data.sale, stockRemaining: data.stockRemaining });
      setSelectedId(""); setQuantity(1); setPayment("cash");
      fetchProducts(); fetchSales();
    } catch { setError("Network error."); }
    finally   { setProcessing(false); }
  };

  const todayTotal = sales.reduce((s, sale) => s + sale.total, 0);

  return (
    <div>
      {/* Terminal */}
      <div className="adm-create-form" style={{ maxWidth: 480 }}>
        <p className="adm-form-title">Record Sale</p>

        <div className="adm-field-group">
          <label className="adm-label">Product</label>
          {loadingP
            ? <p className="adm-loading">Loading products…</p>
            : <select className="adm-input" value={selectedId} onChange={e => { setSelectedId(e.target.value); setError(""); setResult(null); }}>
                <option value="">— select a product —</option>
                {products.map(p => (
                  <option key={p._id} value={p._id}>{p.name} — {fmtMoney(p.price)} ({p.stock} in stock)</option>
                ))}
              </select>
          }
        </div>

        <div className="adm-field-row">
          <div className="adm-field-group">
            <label className="adm-label">Quantity</label>
            <input className="adm-input" type="number" min="1" max={selected?.stock ?? 99} value={quantity}
              onChange={e => setQuantity(Number(e.target.value))} />
          </div>
          <div className="adm-field-group">
            <label className="adm-label">Payment</label>
            <select className="adm-input" value={payment} onChange={e => setPayment(e.target.value)}>
              <option value="cash">Cash</option>
            </select>
          </div>
          <p style={{ fontSize: "0.8rem", color: "var(--color-grey-400)", margin: "0.25rem 0 0" }}>Card processing via Square — coming soon</p>
        </div>

        {selected && (
          <p style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--color-tertiary)", margin: "0.5rem 0 0.75rem" }}>
            Total: {fmtMoney(selected.price * quantity)}
          </p>
        )}

        {error  && <p className="adm-error">{error}</p>}
        {result && (
          <div style={{ background: "#d4f7e7", border: "1px solid #2e7d52", borderRadius: 6, padding: "0.75rem 1rem", marginBottom: "0.75rem" }}>
            <strong style={{ color: "#1a6640" }}>✓ Sale recorded — {fmtMoney(result.sale.total)}</strong>
            <div style={{ fontSize: "0.85rem", color: "#2e7d52", marginTop: "0.25rem" }}>{result.sale.productName} × {result.sale.quantity} · {result.stockRemaining} remaining</div>
          </div>
        )}

        <button className="adm-btn-primary" onClick={handleSell} disabled={processing || !selectedId}>
          {processing ? "Processing…" : "Record Sale"}
        </button>
      </div>

      {/* Today's sales */}
      <div className="adm-roster-header" style={{ marginTop: "2rem" }}>
        <h2 className="adm-section-title">Today's Sales</h2>
        <span className="adm-count-badge">{fmtMoney(todayTotal)}</span>
      </div>

      {loadingS && <p className="adm-loading">Loading…</p>}
      {!loadingS && sales.length === 0 && <p className="adm-empty">No sales today.</p>}
      {!loadingS && sales.length > 0 && (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr><th>Product</th><th>Qty</th><th>Unit Price</th><th>Total</th><th>Payment</th></tr>
            </thead>
            <tbody>
              {sales.map(s => (
                <tr key={s._id}>
                  <td>{s.productName}</td>
                  <td>{s.quantity}</td>
                  <td>{fmtMoney(s.unitPrice)}</td>
                  <td><strong>{fmtMoney(s.total)}</strong></td>
                  <td><span className="adm-type-badge">{s.paymentMethod}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main Tab ──────────────────────────────────────────────
export default function MerchTab() {
  const [section, setSection] = useState("sales");

  return (
    <div className="adm-tab-content">
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem" }}>
        <button
          className={`${section === "sales" ? "adm-btn-primary" : "adm-btn-ghost"}`}
          onClick={() => setSection("sales")}>
          Sales Terminal
        </button>
        <button
          className={`${section === "stock" ? "adm-btn-primary" : "adm-btn-ghost"}`}
          onClick={() => setSection("stock")}>
          Stock
        </button>
      </div>
      {section === "sales" ? <SalesSection /> : <StockSection />}
    </div>
  );
}